/**
 * Silk — smooth flowing satin, kept deliberately understated. Large, slow,
 * low-frequency domain-warped folds rendered in a near-monochrome dark/green so
 * the field reads as faint texture, not colour. The brand green appears only as
 * an undertone where the cursor is, fading straight back to grey with distance.
 *
 * Brand: green localised to the cursor on near-black. Uniform value arrays are
 * plain number[] (no `as const`) so ShaderCanvas can pass them verbatim without
 * re-init churn.
 */

const SIMPLEX_2D = `
vec2 hash2(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
  return -1.0 + 2.0 * fract(sin(p) * 43758.5453123);
}
float snoise(vec2 p) {
  const float K1 = 0.366025404;
  const float K2 = 0.211324865;
  vec2 i = floor(p + (p.x + p.y) * K1);
  vec2 a = p - i + (i.x + i.y) * K2;
  float m = step(a.y, a.x);
  vec2 o = vec2(m, 1.0 - m);
  vec2 b = a - o + K2;
  vec2 c = a - 1.0 + 2.0 * K2;
  vec3 h = max(0.5 - vec3(dot(a, a), dot(b, b), dot(c, c)), 0.0);
  vec3 n = h * h * h * h * vec3(dot(a, hash2(i)), dot(b, hash2(i + o)), dot(c, hash2(i + 1.0)));
  return dot(n, vec3(70.0));
}
float fbm(vec2 p) {
  float v = 0.0;
  float amp = 0.5;
  for (int i = 0; i < 5; i++) {
    v += amp * snoise(p);
    p *= 2.0;
    amp *= 0.5;
  }
  return v;
}
`;

export const SILK_FRAGMENT = `
precision highp float;
uniform float uTime;
uniform vec3 uResolution;
uniform vec2 uMouse;
uniform float uBrightness;
uniform vec3 uColorBase;
uniform vec3 uColorMid;
uniform vec3 uColorHi;
uniform vec3 uColorPurple;
${SIMPLEX_2D}

// Cheap film grain from screen position + time.
float grain(vec2 fragXY, float t) {
  return fract(sin(dot(fragXY + t, vec2(12.9898, 78.233))) * 43758.5453123);
}

void main() {
  vec2 uv = gl_FragCoord.xy / uResolution.xy;
  vec2 p = vec2(uv.x * uResolution.z, uv.y);
  vec2 m = vec2(uMouse.x * uResolution.z, uMouse.y);

  // Slow time so the satin folds drift lazily.
  float t = uTime * 0.035;

  // Cursor influence: soft falloff + direction toward the cursor.
  vec2 toM = p - m;
  float md = length(toM);
  float pull = exp(-md * md * 2.0);
  vec2 dir = normalize(toM + 0.0001);

  // LOW-frequency domain warp -> large, wavy, smooth folds (not busy veins).
  // First warp layer establishes the big swell of the fabric.
  vec2 q = vec2(
    fbm(p * 0.55 + vec2(0.0, t)),
    fbm(p * 0.55 + vec2(4.7, -t) + 1.3)
  );

  // The cursor lifts the nearest folds, nudging the warp outward. Kept gentle so
  // it does not concentrate all the visible silk into a vortex at the pointer
  // (which left the rest of the field — e.g. behind the hero badge — dark).
  q += 0.16 * dir * pull;

  // Second warp layer folds the silk over itself for the satin sheen lines.
  vec2 r = vec2(
    fbm(p * 0.55 + 1.6 * q + vec2(1.7, 9.2) + 0.10 * t),
    fbm(p * 0.55 + 1.6 * q + vec2(8.3, 2.8) - 0.08 * t)
  );

  float f = fbm(p * 0.55 + 1.5 * r);
  float v = clamp(f * 0.5 + 0.5, 0.0, 1.0);

  // Directional sheen: a smooth ridge term that runs along the fold crests
  // (thin-film highlight gliding over satin), modulated by the warp field.
  float crest = smoothstep(0.46, 0.85, v);
  float sheen = pow(crest, 1.5);

  // Barely-there neutral grey satin: very faint folds so the field reads as a
  // whisper of texture, not a surface.
  vec3 col = uColorBase;
  col = mix(col, uColorMid, smoothstep(0.42, 0.86, v) * 0.42);

  // The colour lives on the STREAKS only (the thin sheen riding the crests),
  // never the empty background -> no halo. Near the cursor those streaks turn
  // brand green and pop a touch; away from it they stay a faint cool white.
  float cpull = exp(-md * md * 6.0);
  vec3 streak = mix(uColorHi, uColorPurple, cpull);
  col = mix(col, streak, sheen * (0.14 + 0.42 * cpull));

  col *= uBrightness;

  // Very subtle animated grain (~3%) for premium texture.
  float g = grain(gl_FragCoord.xy, fract(uTime));
  col += (g - 0.5) * 0.03;

  col = max(col, 0.0);
  // High alpha floor: the satin composites as one continuous sheet over the card
  // instead of going transparent in the dark folds (which read as a dark patch
  // behind the hero). Edges are still eased into the bg by the vignette + fade.
  gl_FragColor = vec4(col, clamp(length(col) * 1.2, 0.6, 1.0));
}
`;

export const SILK_UNIFORMS = {
  uBrightness: { value: 1.0 },
  uColorBase: { value: [0.03, 0.075, 0.045] }, // dark green floor
  uColorMid: { value: [0.07, 0.2, 0.115] }, // faint green in the folds
  uColorHi: { value: [0.2, 0.6, 0.33] }, // green streak on the crests
  uColorPurple: { value: [0.28, 0.86, 0.47] }, // brighter green on streaks near cursor
};

export const SILK_SMOOTHING = 0.05; // cursor trail smoothing 0..1
